import { useCallback, useEffect, useMemo, useState } from "react";
import { Drawer, Input, Spin } from "antd";
import { careService } from "../../services/careService";

const summaryGroups = [
  ["immediatePriorities", "Immediate priorities", "ti-flag"],
  ["risksAndWarnings", "Risks & warnings", "ti-shield-exclamation"],
  ["requiredAssistance", "Required assistance", "ti-user-heart"],
  ["reportingInstructions", "Reporting instructions", "ti-clipboard-text"],
];

const fieldCount = (section) =>
  (section?.subsections || []).reduce((total, subsection) => total + (subsection.fields || []).length, 0);

const reviewCount = (section) =>
  (section?.subsections || []).reduce(
    (total, subsection) =>
      total + (subsection.fields || []).filter((field) => field.needsReview || Number(field.confidence) < 0.8).length,
    0,
  );

function sourceLabel(field) {
  const sources = field?.sources || [];
  if (!sources.length) return "—";
  return sources
    .map((source) => {
      if (typeof source === "string") return source;
      const page = source.pageNumber ? `p. ${source.pageNumber}` : "";
      return page || source.fileName || "Source";
    })
    .join(", ");
}

function confidenceLabel(field) {
  const confidence = Number(field?.confidence);
  if (!Number.isFinite(confidence)) return { label: "Not scored", tone: "neutral" };
  if (confidence >= 0.9) return { label: "High", tone: "high" };
  if (confidence >= 0.8) return { label: "Good", tone: "good" };
  if (confidence >= 0.65) return { label: `${Math.round(confidence * 100)}%`, tone: "review" };
  return { label: `${Math.round(confidence * 100)}%`, tone: "low" };
}

function OverviewMetric({ icon, label, value, detail, tone = "" }) {
  return (
    <div className={`extraction-overview-metric ${tone ? `is-${tone}` : ""}`}>
      <i className={`ti ${icon}`} aria-hidden="true" />
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{detail}</small>
      </div>
    </div>
  );
}

function SummaryPanel({ summary }) {
  const visibleGroups = summaryGroups
    .map(([key, title, icon]) => ({ key, title, icon, items: Array.isArray(summary?.[key]) ? summary[key] : [] }))
    .filter((group) => group.items.length);

  if (!visibleGroups.length) return null;

  return (
    <section className="extraction-summary" aria-labelledby="extraction-summary-title">
      <div className="extraction-summary-title">
        <h3 id="extraction-summary-title">Caregiver handoff summary</h3>
        <p>Key information extracted from the assessment for care planning.</p>
      </div>
      <div className="extraction-summary-groups">
        {visibleGroups.map((group) => (
          <div className="extraction-summary-group" key={group.key}>
            <div className="extraction-summary-group-title">
              <i className={`ti ${group.icon}`} aria-hidden="true" />
              <strong>{group.title}</strong>
            </div>
            <ul>
              {group.items.map((item, index) => <li key={`${group.key}-${index}`}>{item}</li>)}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

function SectionDetail({ section, searchTerm }) {
  const term = searchTerm.trim().toLowerCase();
  const subsections = (section?.subsections || [])
    .map((subsection) => ({
      ...subsection,
      fields: (subsection.fields || []).filter((field) =>
        !term || `${field.label || ""} ${field.value || ""}`.toLowerCase().includes(term)),
    }))
    .filter((subsection) => subsection.fields.length);

  if (!subsections.length) {
    return (
      <div className="extraction-empty">
        <i className="ti ti-search" aria-hidden="true" />
        <h3>No matching fields</h3>
        <p>Try a different label or extracted value.</p>
      </div>
    );
  }

  return (
    <div className="extraction-section-detail">
      <header className="extraction-section-heading">
        <div>
          <h2>{section.title || section.sectionKey}</h2>
          <p>{fieldCount(section)} extracted fields{reviewCount(section) ? ` · ${reviewCount(section)} need review` : ""}</p>
        </div>
      </header>
      {subsections.map((subsection, subsectionIndex) => (
        <section className="extraction-subsection" key={`${subsection.title}-${subsectionIndex}`}>
          <h3>{subsection.title && subsection.title !== "Other" ? subsection.title : "Details"}</h3>
          <div className="extraction-field-table">
            <div className="extraction-field-table-head" aria-hidden="true">
              <span>Field</span><span>Extracted value</span><span>Source</span><span>Confidence</span>
            </div>
            {subsection.fields.map((field, fieldIndex) => {
              const confidence = confidenceLabel(field);
              const needsReview = field.needsReview || Number(field.confidence) < 0.8;
              return (
                <div className={`extraction-field-row ${needsReview ? "needs-review" : ""}`} key={`${field.label}-${fieldIndex}`}>
                  <div className="extraction-field-label">{field.label || "Unlabelled field"}</div>
                  <div className="extraction-field-value">{field.value || <em>Not extracted</em>}</div>
                  <div className="extraction-field-source">{sourceLabel(field)}</div>
                  <div className={`extraction-field-confidence is-${confidence.tone}`}>
                    <span aria-hidden="true" />
                    {confidence.label}
                    {needsReview ? <strong>Review</strong> : null}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

export default function AssessmentExtractionDrawer({
  assessmentId,
  open,
  onClose,
  initialDocument,
  onUseInCarePlan,
}) {
  const [extractedDoc, setExtractedDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSectionKey, setSelectedSectionKey] = useState("");

  const fetchDocument = useCallback(() => {
    if (!assessmentId) return;
    setLoading(true);
    setError("");
    careService.assessmentExtractions
      .getExtractedDocument(assessmentId)
      .then((data) => setExtractedDoc(data || null))
      .catch((err) => {
        setExtractedDoc(null);
        setError(err?.response?.data?.message || err?.message || "Unable to load extracted document.");
      })
      .finally(() => setLoading(false));
  }, [assessmentId]);

  useEffect(() => {
    if (!open) return;
    if (initialDocument) setExtractedDoc(initialDocument);
    else fetchDocument();
  }, [open, fetchDocument, initialDocument]);

  const sections = useMemo(() => {
    if (!extractedDoc) return [];
    if (Array.isArray(extractedDoc.sections)) return extractedDoc.sections.filter((section) => fieldCount(section));
    return [];
  }, [extractedDoc]);

  useEffect(() => {
    if (sections.length && !sections.some((section) => section.sectionKey === selectedSectionKey)) {
      setSelectedSectionKey(sections[0].sectionKey);
    }
  }, [sections, selectedSectionKey]);

  const selectedSection = sections.find((section) => section.sectionKey === selectedSectionKey) || sections[0];
  const warnings = extractedDoc?.warnings || extractedDoc?.extractionMetadata?.warnings || [];
  const metadata = extractedDoc?.extractionMetadata || extractedDoc?.metadata || {};
  const summary = extractedDoc?.caregiverSummary || {};
  const totalReview = sections.reduce((total, section) => total + reviewCount(section), 0);
  const processedPages = extractedDoc?.processedPageCount ?? extractedDoc?.pagesProcessed ?? "—";
  const expectedPages = extractedDoc?.expectedPageCount;
  const missingPages = extractedDoc?.missingPageNumbers || [];
  const duration = metadata.durationMillis ? `${Math.round(metadata.durationMillis / 1000)} seconds` : "Completed";

  return (
    <Drawer
      className="assessment-extraction-drawer"
      title={null}
      placement="right"
      size="large"
      closable={false}
      onClose={onClose}
      open={open}
    >
      <div className="extraction-workspace">
        <header className="extraction-workspace-header">
          <div>
            <h1>Assessment extraction</h1>
            <p>Review the extracted details before using them in the negotiated care plan.</p>
          </div>
          <div className="extraction-header-actions">
            <button className="btn secondary" type="button" onClick={() => window.print()}>
              <i className="ti ti-printer" /> Print
            </button>
            <button className="btn primary" type="button" onClick={onUseInCarePlan || onClose}>
              Continue to care plan
            </button>
            <button className="extraction-close" type="button" onClick={onClose} aria-label="Close extraction review">
              <i className="ti ti-x" />
            </button>
          </div>
        </header>

        {loading ? (
          <div className="extraction-loading"><Spin /><p>Loading extracted assessment…</p></div>
        ) : null}
        {error ? <div className="extraction-error"><i className="ti ti-alert-circle" /><p>{error}</p></div> : null}

        {!loading && !error && extractedDoc ? (
          <>
            <section className="extraction-overview" aria-label="Extraction overview">
              <OverviewMetric icon="ti-circle-check" label="Pages processed" value={expectedPages ? `${processedPages} of ${expectedPages}` : processedPages} detail="Assessment pages analysed" tone="success" />
              <OverviewMetric icon="ti-file-alert" label="Missing pages" value={missingPages.length} detail={missingPages.length ? missingPages.join(", ") : "No missing pages detected"} tone={missingPages.length ? "warning" : "success"} />
              <OverviewMetric icon="ti-alert-triangle" label="Items needing review" value={totalReview} detail={totalReview ? "Low-confidence fields" : "No review flags"} tone={totalReview ? "warning" : "success"} />
              <OverviewMetric icon="ti-clock" label="Extraction time" value={duration} detail={metadata.model || metadata.extractionModel || "Gemini extraction"} />
            </section>

            <SummaryPanel summary={summary} />

            {warnings.length ? (
              <section className="extraction-warning-band">
                <i className="ti ti-alert-triangle" aria-hidden="true" />
                <div><strong>Extraction notes</strong>{warnings.map((warning, index) => <p key={index}>{warning}</p>)}</div>
              </section>
            ) : null}

            <div className="extraction-review-layout">
              <aside className="extraction-section-rail">
                <div className="extraction-search">
                  <Input
                    aria-label="Search extracted fields"
                    placeholder="Search extracted fields"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    allowClear
                    prefix={<i className="ti ti-search" />}
                  />
                </div>
                <span className="extraction-rail-label">Sections</span>
                <nav aria-label="Assessment sections">
                  {sections.map((section) => {
                    const count = reviewCount(section);
                    return (
                      <button
                        className={section.sectionKey === selectedSection?.sectionKey ? "is-active" : ""}
                        type="button"
                        key={section.sectionKey}
                        onClick={() => setSelectedSectionKey(section.sectionKey)}
                      >
                        <span>{section.title || section.sectionKey}</span>
                        <small>{fieldCount(section)}</small>
                        {count ? <i className="ti ti-alert-circle" title={`${count} need review`} /> : null}
                      </button>
                    );
                  })}
                </nav>
              </aside>
              <main className="extraction-review-main">
                {selectedSection ? <SectionDetail section={selectedSection} searchTerm={searchTerm} /> : (
                  <div className="extraction-empty"><h3>No assessment fields were extracted</h3><p>Review the source images and try extraction again.</p></div>
                )}
              </main>
            </div>
          </>
        ) : null}

        {!loading && !error && !extractedDoc ? (
          <div className="extraction-empty">
            <i className="ti ti-file-search" />
            <h3>No extracted assessment yet</h3>
            <p>Add assessment images and select Extract data to create the clinical review.</p>
          </div>
        ) : null}
      </div>
    </Drawer>
  );
}
